import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import CHorizontalScrollContainer from '../../../components/CHorizontalScrollContainer';
import CLocalizedTextStringStatic from '../../../components/CLocalizedTextStringStatic';
import CMarkdownText from '../../../components/CMarkdownText';
import CPageModule from '../../../components/CPageModule';
import CPageModulePlot from '../../../components/CPageModulePlot';
import CStatefulContainer from '../../../components/CStatefulContainer';
import CTabButton from '../../../components/CTabButton';
import CTabButtonWrapper from '../../../components/CTabButtonWrapper';
import GenericDateRangeSelector from '../../../components/GenericDateRangeSelector';
import { type IFundPortrait } from '../../../components/IFundPortrait';
import { ETableIds } from '../../../enums/ETableIds';
import { PageRoutes } from '../../../enums/enums';
import { updateStateAttribute } from '../../../helpers/helper';
import { useAppSelector } from '../../../hooks/hooks';
import { type IResponse } from '../../../models/IResponse';
import { type PlotlyResponse } from '../../../models/PlotlyModel';
import { type TPandasTable } from '../../../models/TPandasTable';
import { SvMultifundFactsheetService } from '../../../services/SvMultifundFactsheetService';
import { addBreadcrumbTextReplacement, purgeBreadcrumbTextReplacementDictForOutlets } from '../../../store/appSlice';
import CTable from '../../playground/CTable';

interface State {
  startDate?: dayjs.Dayjs;
  endDate?: dayjs.Dayjs;
  fundPortrait: IResponse<IFundPortrait>;
  performanceTable: IResponse<TPandasTable> & { overlayMessage?: string };
  performanceChart: IResponse<PlotlyResponse> & { overlayMessage?: string };
}

export default function PMultifundFactsheetPerformance() {
  const { locale } = useAppSelector((state) => state.dashboard);
  const dispatch = useDispatch();
  const { simpleFundKey } = useParams();

  const [state, setState] = useState<State>({
    fundPortrait: {
      state: {
        isLoading: false,
        isError: false
      }
    },
    performanceTable: {
      state: {
        isLoading: false,
        isError: false
      }
    },
    performanceChart: {
      state: {
        isLoading: false,
        isError: false
      }
    }
  });

  const pageHeader = CLocalizedTextStringStatic('fsperfP1H1');
  // const pageText = CMarkdownText({ dictKey: 'fsperfP1Desc1' });
  const sinceLaunchLabel = CMarkdownText({ dictKey: 'fsperfP2tab2' });
  const tableDescLabel = CMarkdownText({ dictKey: 'fsperfP1Desc2' });

  const fetchPortrait = async () => {
    if (!simpleFundKey) return;

    updateStateAttribute<State>(
      {
        fundPortrait: {
          ...state.fundPortrait,
          state: {
            ...state.fundPortrait.state,
            isLoading: true
          }
        }
      },
      setState
    );

    const res = await SvMultifundFactsheetService.getFundPortrait(simpleFundKey);

    const isError = !(await res.isSuccess());
    const data = await res.getData();

    updateStateAttribute<State>(
      {
        fundPortrait: {
          state: {
            isLoading: false,
            isError,
            strError: isError ? await res.getMessage() : undefined
          },
          data
        },
        startDate: data ? dayjs(data.launchDateFund) : undefined,
        endDate: dayjs()
      },
      setState
    );
  };

  const fetchTable = async () => {
    updateStateAttribute<State>(
      {
        performanceTable: {
          ...state.performanceTable,
          state: {
            ...state.performanceTable.state,
            isLoading: true,
            isError: false
          }
        }
      },
      setState
    );
    const res = await SvMultifundFactsheetService.getPerformanceTable(
      simpleFundKey ?? '',
      state.startDate ?? dayjs(state.fundPortrait.data?.launchDateFund),
      state.endDate ?? dayjs()
    );
    const isSuccess = await res.isSuccess();
    const data = await res.getData();
    // Masked results are flagged as unsuccessful, but still carry data
    const isError = !isSuccess && !data;
    updateStateAttribute<State>(
      {
        performanceTable: {
          state: {
            isLoading: false,
            strError: isError ? await res.getMessage() : undefined,
            isError
          },
          data,
          overlayMessage: !isSuccess && data ? await res.getMessage() : undefined
        }
      },
      setState
    );
  };

  const fetchChart = async () => {
    updateStateAttribute<State>(
      {
        performanceChart: {
          ...state.performanceChart,
          state: {
            ...state.performanceChart.state,
            isLoading: true,
            isError: false
          }
        }
      },
      setState
    );
    const res = await SvMultifundFactsheetService.getPerformanceChart(
      simpleFundKey ?? '',
      state.startDate ?? dayjs(state.fundPortrait.data?.launchDateFund),
      state.endDate ?? dayjs()
    );
    const isSuccess = await res.isSuccess();
    const data = await res.getData();
    // Masked results are flagged as unsuccessful, but still carry data
    const isError = !isSuccess && !data;
    updateStateAttribute<State>(
      {
        performanceChart: {
          state: {
            isLoading: false,
            strError: isError ? await res.getMessage() : undefined,
            isError
          },
          data,
          overlayMessage: !isSuccess && data ? await res.getMessage() : undefined
        }
      },
      setState
    );
  };

  useEffect(() => {
    (async () => {
      if (state.fundPortrait.data) {
        await Promise.all([fetchTable(), fetchChart()]);
      }
    })();
  }, [state.startDate, state.endDate]);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchPortrait()]);
    })();
    dispatch(addBreadcrumbTextReplacement([PageRoutes.PUBLIC_FACTSHEET_PERFORMANCE, pageHeader]));
    return () => {
      dispatch(purgeBreadcrumbTextReplacementDictForOutlets());
    };
  }, [locale]);

  return (
    <CPageModule fullWidth>
      <CPageModule submodule>
        <h2>{pageHeader}</h2>
        {/* <p>{pageText}</p> */}
        {!!state.performanceChart.data && (
          <div className={'flex justify-between flex-wrap gap-x-2 gap-y-4'}>
            <div className={'flex'}>
              <CTabButtonWrapper>
                <CTabButton
                  text={sinceLaunchLabel}
                  isSelected={
                    state.startDate?.format('YYYY-MM-DD') ===
                      dayjs(state.fundPortrait.data?.launchDateFund).format('YYYY-MM-DD') &&
                    state.endDate?.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD')
                  }
                  label={'sinceLaunch'}
                  onClick={() => {
                    updateStateAttribute<State>(
                      {
                        startDate: dayjs(state.fundPortrait.data?.launchDateFund),
                        endDate: dayjs()
                      },
                      setState
                    );
                  }}
                />
              </CTabButtonWrapper>
            </div>
            <GenericDateRangeSelector
              minDate={dayjs(state.fundPortrait.data?.launchDateFund).unix() * 1000}
              initStartDate={dayjs(state.startDate).unix() * 1000}
              initEndDate={dayjs(state.endDate).unix() * 1000}
              maxDate={dayjs().unix() * 1000}
              onChange={(startUnix, endUnix) => {
                updateStateAttribute<State>(
                  {
                    startDate: dayjs(startUnix),
                    endDate: dayjs(endUnix)
                  },
                  setState
                );
              }}
            />
          </div>
        )}
      </CPageModule>
      <CPageModulePlot
        data={state.performanceChart.data?.data}
        layout={state.performanceChart.data?.layout}
        overlayMessage={
          state.performanceChart.overlayMessage
            ? {
                mdText: state.performanceChart.overlayMessage,
                wordLength: state.performanceChart.overlayMessage.split(' ').length
              }
            : undefined
        }
        apiRequestState={state.performanceChart.state}
        showWhen={!!state.performanceChart.data}
        fitToViewport
      />
      {!!state.performanceChart.data && (
        <>
          <CPageModule submodule>
            <p>{tableDescLabel}</p>
          </CPageModule>
          <CStatefulContainer apiRequestState={state.performanceTable.state} submodule>
            <CHorizontalScrollContainer>
              <CTable
                id={ETableIds.MFFS_PERFORMANCE_ANALYSIS_TABLE}
                data={state.performanceTable.data}
                overlayMessage={
                  state.performanceTable.overlayMessage
                    ? {
                        mdText: state.performanceTable.overlayMessage,
                        wordLength: state.performanceTable.overlayMessage.split('<br/>')[0].split(' ').length
                      }
                    : undefined
                }
                rowConfig={{}}
                columnConfig={Object.keys(state.performanceTable.data ?? {}).map((_, ix) =>
                  ix === 0
                    ? { span: 1.3 }
                    : {
                        header: { style: () => ({ textAlign: 'right' }) },
                        body: { style: () => ({ textAlign: 'right' }) }
                      }
                )}
              />
            </CHorizontalScrollContainer>
          </CStatefulContainer>
        </>
      )}
    </CPageModule>
  );
}
